import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
    Loader2,
    Plus,
    FileDown,
    FileUp,
    X,
    Receipt
} from "lucide-react";

import { DataTable } from "../components/ui/DataTable";
import { fetchPaymentsReceived } from "../apiHelper";
import { useToast } from "../hooks/useToast";
import Modal from "../components/ui/Modal";
import TrialModal from "../components/ui/TrialModal";

const PaymentsReceived = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [user, setUser] = useState(null);
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // State for Floating Action Menu
    const [showActions, setShowActions] = useState(false);

    const [showTrialModal, setShowTrialModal] = React.useState(true); // or use logic like localStorage to show once

    // Load user
    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            showToast({ message: "Please login to view payments.", show: true, toastType: 0 });
            navigate("/login");
            return;
        }
        try {
            const parsedUser = JSON.parse(lcUser);
            setUser(parsedUser);
        } catch {
            showToast({ message: "Invalid session.", show: true, toastType: 2 });
            navigate("/login");
        }
    }, [showToast, navigate]);

    // Fetch payments
    useEffect(() => {
        if (!user?.users_id) return;

        const loadPayments = async () => {
            setLoading(true);
            try {
                const payload = { ...user, offset: 0, limit: 100 };
                const data = await fetchPaymentsReceived(payload);
                // console.log(data)
                setPayments(Array.isArray(data) ? data : []);
            } catch (err) {
                showToast({ message: "Failed to load payments received.", show: true, toastType: 2 });
                setPayments([]);
            } finally {
                setLoading(false);
            }
        };

        loadPayments();
    }, [user]);

    const formatDateTime = (d) => (d ? format(new Date(d), "d MMM yyyy, hh:mm a") : "—");
    const formatDate = (d) => (d ? format(new Date(d), "d MMM yyyy") : "—");
    const formatCurrency = (a) => (a == null ? "₦0.00" : new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN" }).format(a));

    const getPaymentStatus = (payment) => {
        if (!payment) return { label: "Unknown", color: "bg-gray-100 text-gray-700" };
        return payment["Is Paid In Full"]
            ? { label: "Paid in Full", color: "bg-green-100 text-green-800" }
            : { label: "Partial Payment", color: "bg-blue-100 text-blue-800" };
    };

    const openPaymentModal = (payment) => {
        setSelectedPayment(payment);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedPayment(null);
    };

    const columns = [
        { key: "Payment Date", label: "Payment Date", render: (v) => formatDateTime(v) },
        { key: "Invoice Number", label: "Invoice #", render: (_, r) => r["Invoice Number"] || `INV-${r["Order ID"]}` },
        { key: "Customer Name", label: "Customer", render: (_, r) => r["Customer Name"] || "Unknown Customer" },
        { key: "Payment Amount", label: "Payment Amount", render: (v) => formatCurrency(v) },
        { key: "Credit Sale Amount", label: "Invoice Total", render: (_, r) => formatCurrency(r["Credit Sale Amount"]) },
        {
            key: "Current Balance Owed",
            label: "Balance Remaining",
            render: (_, r) => {
                const remaining = r["Current Balance Owed"] - r["Payment Amount"];
                const display = Math.max(0, remaining);
                return (
                    <span className={`font-semibold ${display > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                        {formatCurrency(display)}
                    </span>
                );
            },
        },
        {
            key: "Is Paid In Full",
            label: "Status",
            render: (_, r) => {
                const { label, color } = getPaymentStatus(r);
                return <span className={`px-3 py-1 rounded-full text-xs font-medium ${color}`}>{label}</span>;
            },
        },
        {
            key: "Payment Method",
            label: "Method",
            render: (v) => {
                const methodMap = {
                    bank_transfer: "Bank Transfer",
                    cash: "Cash",
                    card: "Card",
                };
                return methodMap[v] || v || "—";
            },
        },
        { key: "Due Date", label: "Due Date", render: (_, r) => formatDate(r["Due Date"]) },
    ];

    const handleRowAction = (actionKey, row) => {
        if (actionKey === "view") {
            openPaymentModal(row);
        }
    };

    const rowActions = [
        { key: "view", label: "View Details" },
    ];

    const quickActions = [
        { label: "Record Payment", icon: Plus, onClick: () => navigate("/sales/invoices/receive-payment") },
        { label: "Export Payments", icon: FileDown, onClick: () => console.log("Export Payments") },
        { label: "Import Payments", icon: FileUp, onClick: () => console.log("Import Payments") },
    ];

    if (!user) return null;

    return (
        <>
            <div className="min-h-screen bg-gray-50">
                <div className="w-full mx-auto pt-6 px-4 sm:px-6">
                    {/* Header Area */}
                    <div className="mb-6">
                        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                            <Receipt className="text-gmail-blue" />
                            Payments Received
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">
                            Track and manage all incoming customer payments
                        </p>
                    </div>

                    {/* Full-Width Main Content */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        {loading ? (
                            <div className="flex items-center justify-center py-32">
                                <Loader2 className="animate-spin h-12 w-12 text-gmail-blue" />
                            </div>
                        ) : (
                            <DataTable
                                columns={columns}
                                data={payments}
                                title=""
                                rowActions={rowActions}
                                onAction={handleRowAction}
                                classNames="border-none"
                            />
                        )}
                    </div>
                </div>

                {/* --- FLOATING ACTION BUTTON & TOOLTIP MENU --- */}
                <div className="fixed bottom-8 right-8 z-50 flex flex-col items-end">
                    {/* Tooltip-style Action Menu */}
                    {showActions && (
                        <div className="mb-4 flex flex-col gap-4 items-end animate-in fade-in slide-in-from-bottom-4 duration-200">
                            {quickActions.map((action, idx) => (
                                <div key={idx} className="flex items-center gap-3 group">
                                    <span className="bg-gray-800 text-white text-xs px-3 py-1.5 rounded shadow-lg font-medium opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                        {action.label}
                                    </span>
                                    <button
                                        onClick={() => {
                                            action.onClick();
                                            setShowActions(false);
                                        }}
                                        className="p-4 bg-white text-gmail-blue rounded-full shadow-xl hover:bg-blue-50 transition-all border border-gray-100 active:scale-95"
                                        title={action.label}
                                    >
                                        <action.icon size={20} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Toggle Button */}
                    <button
                        onClick={() => setShowActions(!showActions)}
                        className={`flex items-center justify-center w-16 h-16 rounded-full shadow-2xl transition-all duration-300 transform active:scale-90 ${showActions ? "bg-gray-800 rotate-45" : "bg-gmail-blue hover:bg-blue-600"
                            } text-white`}
                    >
                        {showActions ? <X size={32} /> : <Plus size={32} />}
                    </button>

                    {/* Backdrop to close menu */}
                    {showActions && (
                        <div
                            className="fixed inset-0 -z-10 bg-black/10 backdrop-blur-[2px] transition-all"
                            onClick={() => setShowActions(false)}
                        />
                    )}
                </div>
            </div>

            {/* Payment Receipt Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={closeModal}
                title="Payment Receipt"
                size="wide"
                className="font-mono"
            >
                {selectedPayment && (
                    <div className="max-w-2xl mx-auto text-sm leading-relaxed">
                        {/* Receipt Header */}
                        <div className="text-center border-b-2 border-dashed border-gray-400 pb-4 mb-4">
                            <h2 className="text-2xl font-bold text-gray-800">PAYMENT RECEIPT</h2>
                            <p className="text-gray-600 mt-1">Your Company Name Ltd.</p>
                            <p className="text-gray-500">123 Business Street, Lagos, Nigeria</p>
                        </div>

                        {/* Payment Info */}
                        <div className="grid grid-cols-2 gap-4 mb-6">
                            <div>
                                <p className="text-gray-600">Receipt No:</p>
                                <p className="font-semibold">PMT-{selectedPayment["Payment ID"]}</p>
                            </div>
                            <div className="text-right">
                                <p className="text-gray-600">Date & Time:</p>
                                <p className="font-semibold">{formatDateTime(selectedPayment["Payment Date"])}</p>
                            </div>
                        </div>

                        {/* Payment Details Table */}
                        <div className="border-t border-b border-gray-300 py-4 mb-6">
                            <div className="grid grid-cols-2">
                                <span className="text-gray-600">Invoice Total:</span>
                                <span className="font-semibold text-right">{formatCurrency(selectedPayment["Credit Sale Amount"])}</span>

                                <span className="text-gray-600">Amount Paid:</span>
                                <span className="font-bold text-xl text-green-600 text-right">{formatCurrency(selectedPayment["Payment Amount"])}</span>

                                <span className="text-gray-600 font-bold">New Balance:</span>
                                <span className="font-bold text-xl text-right text-orange-600">
                                    {formatCurrency(selectedPayment["Current Balance Owed"])}
                                </span>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="text-center mt-8 pt-4 border-t-2 border-dashed border-gray-400">
                            <p className="text-gray-600 text-xs">Thank you for your payment!</p>
                        </div>
                    </div>
                )}
            </Modal>
            {/* <TrialModal isOpen={showTrialModal} onRequestClose={() => setShowTrialModal(false)} /> */}
        </>
    );
};

export default PaymentsReceived;